import React, { useState } from 'react'
import { Container, Spinner, Toast, Nav } from 'react-bootstrap'
import { useFetchUserProfileQuery } from '../services/authApi'
import { useGetUsersQuery, useDeleteUserMutation } from '../services/userApi'
import { useGetOrganizationPublicQuery } from '../services/orgsApi'
import { User } from '../types/models/user'
import ConfirmDialog from '../components/modals/ConfirmDialog'
import { CreateUserModal, EditUserModal } from '../components/views/AdminDashboard/EditUserModal'
import UsersTable from '../components/views/AdminDashboard/UsersTable'
import OverviewTab from '../components/views/AdminDashboard/components/OverviewTab'
import OrganizationTab from '../components/views/AdminDashboard/components/OrganizationTab'
import ProfileTab from '../components/views/AdminDashboard/components/ProfileTab'
import {
  TabNavContainer,
  TabNav,
  TabContentContainer,
  TabSeparator,
} from '../components/views/AdminDashboard/styles'
import { Users, Building2, BarChart3, User as UserIcon, Mail, Calendar } from 'lucide-react'
import { AdminCard } from '../components/views/AdminDashboard/styles'
import styled from 'styled-components'

const ProfileContainer = styled(Container)`
  padding: 0;
`

const ProfileCard = styled(AdminCard)`
  margin-bottom: 2rem;
`

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(34, 211, 238, 0.4);
  border: 4px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`

const ProfileInfo = styled.div`
  flex: 1;

  h2 {
    color: #ffffff;
    font-weight: 700;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #ffffff 0%, #22d3ee 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .user-role {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`

const InfoItem = styled.div`
  padding: 1.25rem;
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.15);
  border-radius: 12px;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.08);
    border-color: rgba(34, 211, 238, 0.25);
    transform: translateY(-2px);
  }

  .info-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-value {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
    word-break: break-word;
  }
`

const Profile: React.FC = () => {
  const { data: currentUser, isLoading: userLoading } = useFetchUserProfileQuery()
  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery({})
  const { data: orgData, isLoading: orgLoading } = useGetOrganizationPublicQuery()

  const [deleteUser] = useDeleteUserMutation()
  const [activeTab, setActiveTab] = useState<string>('profile')

  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: 'ascending' | 'descending' | null
  }>({ key: '', direction: null })

  const isAdmin = currentUser?.username === 'admin'
  const isLoading = userLoading || (isAdmin && (usersLoading || orgLoading))

  // Admin-only handlers
  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const handleBulkDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      if (selectedUser) {
        await deleteUser(selectedUser.id)
        setSelectedUser(null)
      } else {
        for (const userId of selectedUsers) {
          await deleteUser(userId)
        }
        setSelectedUsers([])
      }
      setShowDeleteModal(false)
    } catch (err) {
      setShowError(true)
      setErrorMessage(
        'Failed to delete user(s): ' +
          ((err as unknown as { data?: { message: string } })?.data?.message || 'Unknown Error')
      )
    }
  }

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const toggleAllUsers = () => {
    const regularUsers = usersData?.users.filter((user) => user.username !== 'admin') || []
    setSelectedUsers((prev) =>
      prev.length === regularUsers.length ? [] : regularUsers.map((user) => user.id)
    )
  }

  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' | null = 'ascending'
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        direction = 'descending'
      } else if (sortConfig.direction === 'descending') {
        direction = null
      }
    }
    setSortConfig({ key, direction })
  }

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()

    if (date.getFullYear() < 2000) {
      return 'Never'
    }

    if (date > now) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const diffTime = startOfToday.getTime() - startOfDate.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return (
        date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        }) + ' Today'
      )
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
  }

  if (isLoading) {
    return (
      <ProfileContainer fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <Spinner animation="border" style={{ color: '#22d3ee' }} />
        </div>
      </ProfileContainer>
    )
  }

  if (!currentUser) {
    return (
      <ProfileContainer fluid>
        <div className="text-center py-5">
          <h4 className="text-white mb-3">Profile Not Found</h4>
          <p className="text-muted">Unable to load your profile information.</p>
        </div>
      </ProfileContainer>
    )
  }

  // Admin view with tabs
  if (isAdmin) {
    const organization = orgData
    const tabs = [
      { key: 'profile', label: 'Profile', icon: <UserIcon size={18} /> },
      { key: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
      { key: 'users', label: 'Users', icon: <Users size={18} /> },
      {
        key: 'organization',
        label: 'Organization',
        icon: <Building2 size={18} />,
        disabled: !organization,
      },
    ]

    const renderTabContent = () => {
      switch (activeTab) {
        case 'profile':
          return <ProfileTab currentUser={currentUser} formatDate={formatDate} />
        case 'overview':
          return (
            usersData?.users && (
              <OverviewTab
                users={usersData.users}
                currentUser={currentUser}
                formatDate={formatDate}
              />
            )
          )
        case 'users':
          return (
            usersData?.users && (
              <UsersTable
                users={usersData.users}
                selectedUsers={selectedUsers}
                sortConfig={sortConfig}
                onSort={handleSort}
                onToggleUserSelection={toggleUserSelection}
                onToggleAllUsers={toggleAllUsers}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                onBulkDelete={handleBulkDelete}
                onCreateUser={() => setShowCreateModal(true)}
                formatDate={formatDate}
                organizations={orgData ? [orgData] : []}
              />
            )
          )
        case 'organization':
          return organization ? <OrganizationTab organization={organization} /> : null
        default:
          return null
      }
    }

    return (
      <ProfileContainer fluid>
        {/* Tab Navigation */}
        <TabNavContainer>
          <TabNav
            activeKey={activeTab}
            onSelect={(k) => k && setActiveTab(k)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {tabs.map((tab, index) => (
              <React.Fragment key={tab.key}>
                {index === 1 && <TabSeparator />}
                <Nav.Item>
                  <Nav.Link
                    eventKey={tab.key}
                    disabled={tab.disabled}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {tab.icon}
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              </React.Fragment>
            ))}
          </TabNav>
        </TabNavContainer>

        {/* Tab Content */}
        <TabContentContainer>{renderTabContent()}</TabContentContainer>

        {/* Modals */}
        <CreateUserModal show={showCreateModal} onHide={() => setShowCreateModal(false)} />
        <EditUserModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          user={selectedUser}
        />

        <ConfirmDialog
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirmRight={confirmDelete}
          title={selectedUser ? 'Confirm Delete User' : 'Confirm Bulk Delete'}
          message={
            selectedUser ? (
              <>
                Are you sure you want to delete user{' '}
                <strong className="text-danger">{selectedUser?.username}</strong>?
                <p className="text-danger mb-0 mt-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>This action cannot be undone.
                </p>
              </>
            ) : (
              <>
                Are you sure you want to delete{' '}
                <strong className="text-danger">{selectedUsers.length} users</strong>?
                <p className="text-danger mb-0 mt-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>This action cannot be undone.
                </p>
              </>
            )
          }
          confirmRightText={selectedUser ? 'Delete User' : `Delete ${selectedUsers.length} Users`}
          cancelText="Cancel"
          variant="danger"
        />

        <Toast
          show={showError}
          onClose={() => setShowError(false)}
          className="position-fixed top-0 end-0 m-4"
          style={{ zIndex: 1000 }}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body>{errorMessage}</Toast.Body>
        </Toast>
      </ProfileContainer>
    )
  }

  // Regular user view
  const profileImage =
    currentUser?.profile_400 || currentUser?.profile_200 || currentUser?.profile_100

  return (
    <ProfileContainer fluid>
      <ProfileCard>
        <div className="card-header">
          <ProfileHeader>
            {profileImage ? (
              <img
                src={`data:image/;base64,${profileImage}`}
                alt="Profile"
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  border: '4px solid rgba(34, 211, 238, 0.3)',
                  objectFit: 'cover',
                  boxShadow: '0 8px 24px rgba(34, 211, 238, 0.4)',
                }}
              />
            ) : (
              <ProfileAvatar>{currentUser.first_name?.[0] || <UserIcon size={60} />}</ProfileAvatar>
            )}
            <ProfileInfo>
              <h2>{`${currentUser.first_name} ${currentUser.last_name}`}</h2>
              <div className="user-role">
                <UserIcon size={18} />
                {currentUser.username}
              </div>
            </ProfileInfo>
          </ProfileHeader>
        </div>
        <div className="card-body">
          <InfoGrid>
            <InfoItem>
              <div className="info-label">
                <Mail size={16} />
                Email
              </div>
              <div className="info-value">{currentUser.email}</div>
            </InfoItem>
            <InfoItem>
              <div className="info-label">User ID</div>
              <div className="info-value" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {currentUser.id}
              </div>
            </InfoItem>
            <InfoItem>
              <div className="info-label">
                <Calendar size={16} />
                Account Created
              </div>
              <div className="info-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {formatDate(currentUser.created_at)}
              </div>
            </InfoItem>
            <InfoItem>
              <div className="info-label">
                <Calendar size={16} />
                Last Login
              </div>
              <div className="info-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {formatDate(currentUser.last_login || '')}
              </div>
            </InfoItem>
            {currentUser.last_password_change && (
              <InfoItem>
                <div className="info-label">
                  <Calendar size={16} />
                  Last Password Change
                </div>
                <div className="info-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {formatDate(currentUser.last_password_change)}
                </div>
              </InfoItem>
            )}
          </InfoGrid>
        </div>
      </ProfileCard>
    </ProfileContainer>
  )
}

export default Profile
